import { useMemo, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { DatePicker } from "@/components/ui/DatePicker";
import { NativeSelectField } from "@/components/native/NativeSelectField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DeliveryMethodSelectorProps {
  value: "pickup" | "delivery";
  onChange: (value: "pickup" | "delivery") => void;
  onDateChange?: (date: Date | undefined) => void;
  onTimeChange?: (time: string) => void;
  selectedDate?: Date | undefined;
  selectedTime?: string;
  isEditMode?: boolean;
}

export const DeliveryMethodSelector = ({
  value,
  onChange,
  onDateChange,
  onTimeChange,
  selectedDate: externalSelectedDate,
  selectedTime: externalSelectedTime,
  isEditMode = false,
}: DeliveryMethodSelectorProps) => {
  const { language } = useLanguage();
  const db = ordersLabels(language).deliveryBooking;
  const { baseText, isDark, subtleText } = useTextColorClass();
  const { isNative } = useNativePlatform();
  const [pickupDate, setPickupDate] = useState<Date | undefined>(undefined);
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [pickupTime, setPickupTime] = useState<string>("12:00");
  const [deliveryTime, setDeliveryTime] = useState<string>("12:00");

  const minBookingDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 2);
    return minDate;
  }, []);

  const selectedDate =
    externalSelectedDate !== undefined
      ? externalSelectedDate
      : value === "pickup"
        ? pickupDate
        : deliveryDate;
  const selectedTime =
    externalSelectedTime !== undefined
      ? externalSelectedTime
      : value === "pickup"
        ? pickupTime
        : deliveryTime;

  const setSelectedDate = (date: Date | undefined) => {
    if (value === "pickup") {
      setPickupDate(date);
    } else {
      setDeliveryDate(date);
    }
    onDateChange?.(date);
  };

  const setSelectedTime = (time: string) => {
    if (value === "pickup") {
      setPickupTime(time);
    } else {
      setDeliveryTime(time);
    }
    onTimeChange?.(time);
  };

  const timeOptions = [];
  for (let hour = 8; hour < 20; hour++) {
    timeOptions.push(`${hour}:00`);
    timeOptions.push(`${hour}:30`);
  }

  const timeSelectOptions = timeOptions.map((t) => ({ value: t, label: t }));

  return (
    <div className="space-y-4">
      <div className={`font-semibold ${baseText}`}>{db.chooseMethod}</div>
      <div className="flex space-x-1 p-1 rounded-lg bg-muted w-fit">
        <Button
          variant={value === "pickup" ? "default" : "ghost"}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onChange("pickup");
          }}
        >
          {db.pickup}
        </Button>
        <Button
          variant={value === "delivery" ? "default" : "ghost"}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onChange("delivery");
          }}
        >
          {db.homeDelivery}
        </Button>
      </div>

      {value && (
        <div className={`mt-6 ${baseText}`}>
          <div className="font-semibold mb-2">
            {value === "pickup" ? db.choosePickupDate : db.chooseDeliveryDate}
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-start">
            <div
              className="w-full md:w-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                minDate={minBookingDate}
                placeholder={db.chooseDate}
                triggerClassName={cn(
                  "w-full md:w-auto",
                  !selectedDate && (isDark ? "text-gray-400" : "text-gray-500"),
                )}
              />
            </div>

            <div className="flex items-center gap-2 flex-1 w-full min-w-0">
              <Clock className="h-4 w-4 opacity-70 flex-shrink-0" />
              {isNative ? (
                <NativeSelectField
                  value={selectedTime}
                  onValueChange={setSelectedTime}
                  options={timeSelectOptions}
                  title={db.chooseTime}
                  placeholder={db.chooseTime}
                  triggerClassName="w-full md:w-[150px]"
                />
              ) : (
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger
                    className="w-full md:w-[150px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SelectValue placeholder={db.chooseTime} />
                  </SelectTrigger>
                  <SelectContent
                    className="z-50 bg-popover"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {timeOptions.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {selectedDate && selectedTime && (
            <div className="mt-2 space-y-1">
              <p className={`text-sm ${subtleText}`}>
                {value === "pickup"
                  ? db.youChosePickup
                  : db.yourCarWillBeDelivered}{" "}
                {format(selectedDate, "yyyy-MM-dd")} kl. {selectedTime}
              </p>
            </div>
          )}

          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>{db.importantInfo}</strong>{" "}
              {isEditMode
                ? db.importantInfoTextUpdate
                : db.importantInfoText}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
